import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Book } from '@/types/book';

export interface AudiobookPage {
  id: string; // bookId-trackKey
  bookId: number;
  pageNumber: number;
  audioUrl: string;
  imageUrl: string;
  bookTitle: string;
  author: string;
  pageText?: string; // First line of page content
  fullContent?: string; // Full page content for lyrics display
}

export interface Audiobook {
  id: number;
  title: string;
  author: string;
  coverUrl: string;
  isPreview: boolean;
  pages: AudiobookPage[];
  totalPages: number;
  reviewCount: number;
}

function getStatusSubtitle(book: Book): string {
  const status = book.bundle?.status;
  if (status === 'upcoming') return 'Coming Soon';
  if (status === 'expired') return 'Expired';
  return book.author || 'BuxBox';
}

function parseTrackNumber(filename: string): number {
  // Supports: "01_...mp3" or "1_...mp3" or "page-1.mp3" etc.
  const leading = filename.match(/^(\d{1,3})/);
  if (leading) return parseInt(leading[1], 10);
  const page = filename.match(/page-(\d{1,4})/i);
  if (page) return parseInt(page[1], 10);
  return Number.POSITIVE_INFINITY;
}

function getFirstWords(text: string | undefined | null, wordLimit: number = 4): string {
  if (!text) return '';
  const words = text.trim().split(/\s+/).slice(0, wordLimit);
  return words.join(' ');
}

export function useAudiobooks(allBooks: Book[]) {
  return useQuery({
    queryKey: ['roxbox-audiobooks', allBooks.map((b) => b.id).join(',')],
    enabled: Array.isArray(allBooks) && allBooks.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - match books cache
    gcTime: 30 * 60 * 1000,   // 30 minutes cache  
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Audiobook[]> => {
      // Include books with a bundle OR preview books (free books may have no bundle)
      const books = (Array.isArray(allBooks) ? allBooks : []).filter(b => b.bundle_id != null || b.is_preview === true);

      // Fetch page_images, compiled_book, description, and paragraphs_per_page for all books
      const bookIds = books.map(b => b.id);
      const { data: booksWithData } = await supabase
        .from('books')
        .select('id, page_images, compiled_book, description, paragraphs_per_page')
        .in('id', bookIds);

      const imagesByBookId = new Map<number, any[]>();
      const compiledByBookId = new Map<number, any>();
      const descriptionByBookId = new Map<number, string>();
      const paragraphsPerPageByBookId = new Map<number, number>();
      
      booksWithData?.forEach(b => {
        if (Array.isArray(b.page_images)) {
          imagesByBookId.set(b.id, b.page_images);
        }
        if (b.compiled_book) {
          compiledByBookId.set(b.id, b.compiled_book);
        }
        if (b.description) {
          descriptionByBookId.set(b.id, b.description);
        }
        if (b.paragraphs_per_page) {
          paragraphsPerPageByBookId.set(b.id, b.paragraphs_per_page);
        }
      });

      const audiobooks = await Promise.all(
        books.map(async (book) => {
          const folder = String(book.id);
          const { data: files, error } = await supabase.storage
            .from('book-media')
            .list(folder, { limit: 200 });

          if (error) {
            return {
              id: Number(book.id),
              title: book.title,
              author: getStatusSubtitle(book),
              coverUrl: book.cover_image_url || '',
              isPreview: book.is_preview === true,
              pages: [],
              totalPages: 0,
              reviewCount: book.review_count || 0,
            } satisfies Audiobook;
          }

          const mp3s = (files || [])
            .filter((f) => {
              if (!f.name.toLowerCase().endsWith('.mp3')) return false;
              // Skip track 00 (intro/credits)
              const trackNum = parseTrackNumber(f.name);
              return trackNum > 0;
            })
            .sort((a, b) => {
              const an = parseTrackNumber(a.name);
              const bn = parseTrackNumber(b.name);
              if (an !== bn) return an - bn;
              return a.name.localeCompare(b.name);
            });

          const coverUrl = book.cover_image_url || '';
          
          // Get page images from page_images array (pageName: "page-1", url: "...")
          const pageImagesArray = imagesByBookId.get(Number(book.id)) || [];
          
          // Get book content from description and split into pages
          const description = descriptionByBookId.get(Number(book.id)) || '';
          const paragraphsPerPage = paragraphsPerPageByBookId.get(Number(book.id)) || 2;
          
          // Split description into paragraphs and group into pages (same logic as reader)
          const allParagraphs = description
            .split(/\n\s*\n/)
            .filter(p => p.trim().length > 0)
            .map(p => p.trim());
          
          // Group paragraphs into content pages
          const contentPages: string[] = [];
          for (let i = 0; i < allParagraphs.length; i += paragraphsPerPage) {
            const pageParagraphs = allParagraphs.slice(i, i + paragraphsPerPage);
            contentPages.push(pageParagraphs.join('\n\n'));
          }

          const pages: AudiobookPage[] = mp3s.map((f, idx) => {
            const trackNum = parseTrackNumber(f.name);
            const pageNumber = Number.isFinite(trackNum) ? trackNum : idx + 1;
            const path = `${folder}/${f.name}`;
            const audioUrl = supabase.storage.from('book-media').getPublicUrl(path).data.publicUrl;

            // Audio mapping matches the reader:
            // - Audio 01_*.mp3 (trackNum=1) = Cover page, uses cover image
            // - Audio 02_*.mp3 (trackNum=2) = Content page 1, uses page-1.webp
            // - Audio 03_*.mp3 (trackNum=3) = Content page 2, uses page-2.webp
            // So for image: trackNum 1 = cover, trackNum N (N>1) = page-(N-1)
            let pageImageUrl = coverUrl;
            if (pageNumber > 1) {
              const contentPageNumber = pageNumber - 1; // Audio 02 = page-1 image
              const pageName = `page-${contentPageNumber}`;
              const matchingImage = pageImagesArray.find((img: any) => img.pageName === pageName);
              if (matchingImage?.url) {
                pageImageUrl = matchingImage.url;
              }
            }

            // Get page content from description-based pages
            // Audio 01 = cover (title), Audio 02 = contentPages[0], Audio 03 = contentPages[1], etc.
            let pageText = '';
            let fullContent = '';
            
            if (pageNumber === 1) {
              // Cover page - use book title
              pageText = book.title;
              fullContent = `Welcome to\n\n${book.title}\n\nby ${book.author}`;
            } else {
              // Content pages: audio 02 = contentPages[0], audio 03 = contentPages[1], etc.
              const contentPageIndex = pageNumber - 2;
              const pageContent = contentPages[contentPageIndex];
              if (pageContent) {
                pageText = getFirstWords(pageContent, 4);
                fullContent = pageContent;
              }
            }

            return {
              id: `${book.id}-${f.name}`,
              bookId: Number(book.id),
              pageNumber,
              audioUrl,
              imageUrl: pageImageUrl,
              bookTitle: book.title,
              author: book.author || 'BuxBox',
              pageText,
              fullContent,
            };
          });

          return {
            id: Number(book.id),
            title: book.title,
            author: getStatusSubtitle(book),
            coverUrl,
            isPreview: book.is_preview === true,
            pages,
            totalPages: pages.length,
            reviewCount: book.review_count || 0,
          } satisfies Audiobook;
        })
      );

      // Filter out books with no audio, then sort: free/preview books first, then alphabetically
      return audiobooks
        .filter(a => a.pages.length > 0)
        .sort((a, b) => {
          if (a.isPreview && !b.isPreview) return -1;
          if (!a.isPreview && b.isPreview) return 1;
          return a.title.localeCompare(b.title);
        });
    },
  });
}
